import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon } from 'react-bootstrap-icons';
import { apiUrl } from '../../config/api';
import './SearchBox.scss';

const DEBOUNCE_MS = 250;

/**
 * Product search with type-ahead suggestions.
 *
 * Requests are debounced and superseded ones are aborted, so typing quickly
 * no longer fires one request per keystroke or lets a slow earlier response
 * overwrite a newer one.
 */
const SearchBox = ({ autoFocus = false, onNavigate }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Debounced, abortable suggestion lookup.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestions([]);
      setIsOpen(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          apiUrl(`/api/products/suggestion/${encodeURIComponent(trimmed)}`),
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        const results = data.products || [];
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setActiveIndex(-1);
      } catch (error) {
        if (error.name === 'AbortError') return;
        setSuggestions([]);
        setIsOpen(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Close the suggestion list on an outside click.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen]);

  const runSearch = useCallback(
    (term) => {
      const trimmed = (term || '').trim();
      setQuery('');
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      onNavigate?.();
      navigate(
        trimmed ? `/search/${encodeURIComponent(trimmed)}` : '/allproducts'
      );
    },
    [navigate, onNavigate]
  );

  const handleKeyDown = (event) => {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(
        (index) => (index - 1 + suggestions.length) % suggestions.length
      );
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      runSearch(suggestions[activeIndex].product_name);
    }
  };

  return (
    <div className="ij-search" ref={containerRef}>
      <form
        className="ij-search__form"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          runSearch(query);
        }}
      >
        <label htmlFor="ij-search-input" className="ij-visually-hidden">
          Search products
        </label>
        <input
          id="ij-search-input"
          ref={inputRef}
          type="search"
          className="ij-search__input"
          placeholder="Search for jewellery…"
          value={query}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="ij-search-suggestions"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        />
        <button type="submit" className="ij-search__submit" aria-label="Search">
          <SearchIcon size={18} aria-hidden="true" />
        </button>
      </form>

      {isOpen && suggestions.length > 0 && (
        <ul
          className="ij-search__suggestions"
          id="ij-search-suggestions"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.product_code ?? suggestion.product_name}>
              <button
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={`ij-search__suggestion${
                  index === activeIndex ? ' is-active' : ''
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => runSearch(suggestion.product_name)}
              >
                <SearchIcon size={14} aria-hidden="true" />
                <span>{suggestion.product_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
